import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Keys from '@/constants/Keys';
import { applyFilters } from '@/utils/filters';
import { structuredDataForResource } from '@/utils/structuredData';

// Per-resource SEO tags, matching what the Angular resource controller set via
// $rootScope.ogUrl/ogTitle/ogDescription + the StructuredData service. Rendered
// during SSR so crawlers see the tags without executing JS. The `key` props line
// up with the defaults in pages/_app.js so next/head replaces them per-page.
const SeoMetadata = ({ resource }) => {
  if (!resource?.slug) return null;

  const isOrganization = resource.resource_type === 'Organization';
  const path = isOrganization ? `/org/${resource.slug}` : `/opp/${resource.slug}`;

  let title = resource.title || resource.name;
  if (!isOrganization && resource.organization?.name) {
    title = `${title} - ${resource.organization.name}`;
  }

  const description = applyFilters([['truncate', 140]], resource.description);
  // XSS prevention: escape every "<" so resource text (title, description, etc.) can't
  // break out of this JSON-LD <script> block via a substring like "</script>" and get
  // parsed as live HTML.
  const jsonLd = JSON.stringify(structuredDataForResource(resource)).replace(/</g, '\\u003c');

  return (
    <Head>
      <meta key="description" name="description" content={description} />
      <meta key="og:url" property="og:url" content={`${Keys.odas.host}${path}`} />
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:description" property="og:description" content={description} />
      <script key="json-ld" type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </Head>
  );
};

SeoMetadata.propTypes = {
  resource: PropTypes.object
};

export default SeoMetadata;
